// Used in teacher-class-form.html

import {
    Component,
    EventEmitter,
    ViewChild,
    ElementRef,
    HostListener,
    Input,
    OnInit,
    Output,
} from "@angular/core";
import { ValueList } from "#models/valueList.interface";
export interface SelectedIdsInterface {
    selectedId: string | number;
}
@Component({
    selector: "multiple-values-selector",
    templateUrl: "./multiple-values-selector.component.html",
    styleUrls: ["./multiple-values-selector.component.scss"],
    standalone: false
})
export class MultipleValuesSelectorComponent implements OnInit {
    elementId: string =
        "valueSelector" +
        (Math.random() * 100000).toFixed(0) +
        "_" +
        (Math.random() * 100000).toFixed(0);
    maxHeight = 500;
    extendDown = true;
    @Input() selectingValueOptions: boolean;
    @Input() selectedIDs: SelectedIdsInterface[];
    @Input() valueList: ValueList[];
    @Input() placeholderValue: string;
    @Input() setWidth: string;
    @Input() maxWidth: string;
    @Input() currentSelections: ValueList[];
    @Input() currentTabbedSelection: ValueList;
    @Input() savedCurrentSelections: ValueList[];
    @Input() disabled: boolean;
    @Input() valueSelectorID: string;
    @Output() currentSelectionChange = new EventEmitter<number>();
    @Output() saveMultipleSelections = new EventEmitter<ValueList[]>();
    @Output() focusLost = new EventEmitter<boolean>(); // focus gained?
    @ViewChild("valueSelectContainer") private valueSelectContainer: ElementRef;

    constructor() {}

    @HostListener("document:click", ["$event"])
    onClick(event: MouseEvent) {
        if (!this.valueSelectContainer.nativeElement.contains(event.target)) {
            this.selectingValueOptions = false; // if the click didn't emanate from within this element, close it.
        }
    }

    ngOnInit(): void {
        // Initial setup for provided list and stop to start when tabbing/arrowing
        const selectedSelection: ValueList[] = [];
        this.selectedIDs.forEach((id) => {
            const foundSelection = this.valueList?.find(
                (x) => x.id === id.selectedId
            );
            if (foundSelection) {
                selectedSelection.push(foundSelection);
            }
        });

        const foundChecked = this.valueList?.find((v) => v.checked);
        if (foundChecked) {
            this.currentTabbedSelection = foundChecked;
        } else {
            this.currentTabbedSelection = this.valueList[0];
        }

        if (selectedSelection) {
            this.currentSelections = selectedSelection;
        }
        this.savedCurrentSelections = this.currentSelections;
    }

    setSelectingValueOptions() {
        this.selectingValueOptions = !this.selectingValueOptions;
        this.selectSizer();
    }

    setCurrentSelection(option: ValueList, index: number) {
        // If the default option is selected, reset the list
        if (option.value === "-select-") {
            this.currentSelections = [option];
            this.savedCurrentSelections = [option];
            const selectedId = { selectedId: option.id };
            this.selectedIDs = [selectedId];
            this.valueList.forEach((v) => {
                v.checked = false;
            });
            this.selectingValueOptions = false;
        }
        this.currentSelectionChange.emit(index);
    }

    // Triggered by checkbox input mouse click only
    onValueListSelectToggle(event: Event) {
        if (this.currentSelections[0].value === "-select-") {
            this.currentSelections.splice(0, 1);
            this.savedCurrentSelections = this.currentSelections;
            this.selectedIDs.splice(0, 1);
        }
        const target =
            event.target instanceof HTMLInputElement ? event.target : undefined;
        const checked = target ? target.checked : undefined;
        const selectedID = target ? target.defaultValue : undefined;
        const foundItem = this.valueList.find((x) => x.id === selectedID);

        const foundIndex = this.currentSelections.findIndex(
            (x) => x.id === selectedID
        );
        if (!checked && foundIndex >= 0) {
            this.currentSelections.splice(foundIndex, 1);
            this.savedCurrentSelections = this.currentSelections;
            this.selectedIDs.splice(foundIndex, 1);
        } else if (foundItem) {
            this.currentSelections.push(foundItem);
            this.savedCurrentSelections = this.currentSelections;
            if (typeof foundItem.id === "number") {
                const selectedId = { selectedId: foundItem.id };
                this.selectedIDs.push(selectedId);
            }
        }

        if (this.currentSelections && this.currentSelections.length === 0) {
            this.currentSelections.push({
                value: "-select-",
                id: "noSecondaryTeacherSelected",
            });
            this.savedCurrentSelections = this.currentSelections;
            this.selectedIDs = [{ selectedId: "noSecondaryTeacherSelected" }];
        }
        this.saveMultipleSelections.emit(this.currentSelections);
    }
    // triggered by "enter" keyboard click only
    onValueListTabbedToggle(valueList: ValueList) {
        this.handleNonCheckboxClick(valueList);
    }
    // triggered by clicking on the value name
    onSelectValueRowClick(valueList: ValueList) {
        this.handleNonCheckboxClick(valueList);
    }

    // When not using the checkbox input to select a value (clicking on the name and using the keyboard)
    handleNonCheckboxClick(valueList: ValueList) {
        if (valueList.value === "-select-") {
            this.currentSelections = [
                {
                    value: "-select-",
                    id: "noSecondaryTeacherSelected",
                },
            ];
            this.savedCurrentSelections = this.currentSelections;
            this.selectedIDs = [{ selectedId: "noSecondaryTeacherSelected" }];
        } else {
            // find select value and remove it
            const foundDefaultValueIndex = this.currentSelections.findIndex(
                (vl) => vl.value === "-select-"
            );
            if (!foundDefaultValueIndex && foundDefaultValueIndex === 0) {
                this.currentSelections.splice(foundDefaultValueIndex, 1);
                this.selectedIDs.splice(foundDefaultValueIndex, 1);
            }

            const foundSelectedIdsIndex = this.selectedIDs.findIndex(
                (i) => i.selectedId === valueList.id
            );
            const foundValueListIndex = this.valueList.findIndex(
                (vl) => vl.id === valueList.id
            );
            if (foundSelectedIdsIndex === -1) {
                this.valueList[foundValueListIndex].checked = true;
                this.selectedIDs.push({ selectedId: valueList.id });
                this.currentSelections.push(valueList);
            } else {
                this.currentSelections.splice(foundSelectedIdsIndex, 1);
                this.selectedIDs.splice(foundSelectedIdsIndex, 1);
                this.valueList[foundValueListIndex].checked = false;
            }
            this.savedCurrentSelections = this.currentSelections;

            // If selected list is now empty, add default back in
            if (this.currentSelections && this.currentSelections.length === 0) {
                this.currentSelections = [
                    {
                        value: "-select-",
                        id: "noSecondaryTeacherSelected",
                    },
                ];
                this.savedCurrentSelections = this.currentSelections;
                this.selectedIDs = [
                    { selectedId: "noSecondaryTeacherSelected" },
                ];
            }
        }
        this.saveMultipleSelections.emit(this.savedCurrentSelections);
    }

    getCurrentSelectionName(): string | undefined {
        if (this.currentSelections) {
            if (this.currentSelections.length === 1) {
                return this.currentSelections[0].value;
            } else {
                // Sorts list by asc order
                this.currentSelections = this.currentSelections.sort((a, b) =>
                    a.value.toLowerCase() > b.value.toLowerCase()
                        ? 1
                        : b.value.toLowerCase() > a.value.toLowerCase()
                        ? -1
                        : 0
                );
                let valueString = "";
                this.currentSelections.forEach((s, index) => {
                    if (index > 0 && index < this.currentSelections.length) {
                        valueString = valueString + ", " + s.value;
                    } else {
                        valueString = valueString + " " + s.value;
                    }
                });
                return valueString;
            }
        } else if (this.savedCurrentSelections) {
            if (this.savedCurrentSelections.length === 1) {
                return this.savedCurrentSelections[0].value;
            } else {
                // Sorts list by asc order
                this.savedCurrentSelections = this.savedCurrentSelections.sort(
                    (a, b) =>
                        a.value.toLowerCase() > b.value.toLowerCase()
                            ? 1
                            : b.value.toLowerCase() > a.value.toLowerCase()
                            ? -1
                            : 0
                );
                let valueString = "";
                this.savedCurrentSelections.forEach((s, index) => {
                    if (
                        index > 0 &&
                        index < this.savedCurrentSelections.length
                    ) {
                        valueString = valueString + ", " + s.value;
                    } else {
                        valueString = valueString + " " + s.value;
                    }
                });
                return valueString;
            }
        } else {
            return "-select-";
        }
    }

    showOptions() {
        if (this.selectingValueOptions && !this.disabled) {
            return true;
        }
    }

    keyHandler(event: KeyboardEvent): void {
        const keyCode: string = event.code.toLowerCase();
        if (!this.disabled) {
            if (this.selectingValueOptions) {
                const options: HTMLCollection =
                    this.valueSelectContainer.nativeElement.getElementsByClassName(
                        "value-selector-option"
                    );
                let selectedIndex = 0;
                let moveInc = 0;
                let newIndex = 0;
                if (["tab", "escape"].includes(keyCode)) {
                    this.selectingValueOptions = false;
                    this.focusLost.emit(true); // tabbed away or esc...just close it and emit
                } else if (
                    ["arrowdown", "arrowup", "home", "end"].includes(keyCode) &&
                    this.valueList.length
                ) {
                    event.preventDefault(); // prevent containing page from being scrolled by arrows, home, or end
                    if (["home", "end"].includes(keyCode)) {
                        newIndex =
                            keyCode === "home" ? 0 : this.valueList.length - 1;
                    } else {
                        selectedIndex = !this.currentSelections
                            ? 0
                            : this.valueList.findIndex(
                                  (vl) =>
                                      vl.id === this.currentTabbedSelection.id
                              );
                        moveInc = keyCode === "arrowup" ? -1 : 1;
                        if (
                            selectedIndex + moveInc <=
                                this.valueList.length - 1 &&
                            selectedIndex + moveInc >= 0
                        ) {
                            newIndex = selectedIndex + moveInc;
                        } else if (
                            selectedIndex + moveInc >
                            this.valueList.length - 1
                        ) {
                            newIndex = 0;
                        } else if (selectedIndex + moveInc < 0) {
                            newIndex = this.valueList.length - 1;
                        }
                    }
                    this.currentTabbedSelection = this.valueList[newIndex];
                    this.scrollToOption(options[newIndex + 1]);
                } else if (
                    [
                        "a",
                        "b",
                        "c",
                        "d",
                        "e",
                        "f",
                        "g",
                        "h",
                        "i",
                        "j",
                        "k",
                        "l",
                        "m",
                        "n",
                        "o",
                        "p",
                        "q",
                        "r",
                        "s",
                        "t",
                        "u",
                        "v",
                        "w",
                        "x",
                        "y",
                        "z",
                    ].includes(event.key) &&
                    this.valueList.length
                ) {
                    newIndex = this.valueList.findIndex(
                        (vl) => vl.value.toLowerCase().indexOf(event.key) === 0
                    );
                    // TODO: Make this more robust? For now, only care about the first letter.
                    //  Making this catch longer strings, will entail acrobatics involving a timeout.
                    if (newIndex > 0) {
                        this.currentTabbedSelection = this.valueList[newIndex];
                        this.scrollToOption(options[newIndex + 1]);
                    }
                } else if (["enter", "space"].includes(keyCode)) {
                    this.setCurrentSelection(this.currentTabbedSelection, -1);
                    this.onValueListTabbedToggle(this.currentTabbedSelection);
                }
            } else {
                if (["enter", "space"].includes(keyCode)) {
                    this.selectingValueOptions = true; // just open it
                    this.setCurrentSelection(this.currentTabbedSelection, -1);
                    this.selectSizer();
                } else if (keyCode === "tab") {
                    this.focusLost.emit(true); // tabbed away without even opening it, emit...
                }
            }
        }
    }

    selectSizer(): void {
        let containerHeight: number, testHeight: number;
        const elem = this.valueSelectContainer.nativeElement,
            boundingRect: DOMRect = elem.getBoundingClientRect(),
            winHeight: number =
                window.innerHeight && window.innerHeight < window.screen.height
                    ? window.innerHeight
                    : window.screen.height, // use whichever is smaller
            topPos: number = boundingRect.y ? boundingRect.y : boundingRect.top;
        containerHeight =
            (boundingRect.height ? boundingRect.height : elem.offsetHeight) +
            10; // 10 more pixels for a little breathing room
        testHeight =
            Math.floor(winHeight) -
            Math.ceil(topPos) -
            Math.ceil(containerHeight);
        if (testHeight < 200) {
            this.extendDown = false;
            this.maxHeight = topPos - containerHeight;
        } else {
            this.extendDown = true;
            this.maxHeight = testHeight;
        }
    }

    scrollToOption(element: Element): void {
        element.scrollIntoView();
    }

    maxHeightReturn(): { [index: string]: string } {
        return { "max-height": this.maxHeight + "px" };
    }

    resetSelector(id: string) {
        const currentSelections = {
            value: "-select-",
            id: id,
        };
        this.currentSelections = [currentSelections];
        this.savedCurrentSelections = [currentSelections];
        const selectedId = { selectedId: currentSelections.id };
        this.selectedIDs = [selectedId];
        this.valueList.forEach((v) => {
            v.checked = false;
        });
        this.selectingValueOptions = false;
    }
}
